import type { ItemType } from '@/interfaces/hotel-list-types/hotel-list-types';
import type { DiscountHotelPriceOffer } from '@/interfaces/neusta-discount-price';
import { getUniqueKey } from '../common/services/bestOfferForHotelListService';
import { determineClient } from '@/js/utils/environmentUtils';

function convertToSwissFranc(price: number | undefined, exchangeRate: number): number {
	return price ? price * exchangeRate : 0;
}

function calculateRelativeDiscountPercent(currentPrice: number, referencePrice: { minPrice: number; maxPrice: number; avgPrice: number }) {
	return {
		toMinPrice:
			referencePrice.minPrice > currentPrice
				? Math.round(((referencePrice.minPrice - currentPrice) / referencePrice.minPrice) * 100)
				: 0,
		toMaxPrice: Math.round(((referencePrice.maxPrice - currentPrice) / referencePrice.maxPrice) * 100),
		toAvgPrice: Math.round(((referencePrice.avgPrice - currentPrice) / referencePrice.avgPrice) * 100),
	};
}

function convertReferencePriceTotal(referencePriceTotal: NonNullable<ItemType['WasPrice']>['referencePriceTotal'], exchangeRate: number) {
	if (!referencePriceTotal) return null;

	return {
		minPrice: convertToSwissFranc(referencePriceTotal.minPrice, exchangeRate),
		maxPrice: convertToSwissFranc(referencePriceTotal.maxPrice, exchangeRate),
		avgPrice: convertToSwissFranc(referencePriceTotal.avgPrice, exchangeRate),
	};
}

function calculateDiscountPercent(
	currentTotalPrice: number,
	convertedReferencePriceTotal: { minPrice: number; maxPrice: number; avgPrice: number }
) {
	return calculateRelativeDiscountPercent(currentTotalPrice, convertedReferencePriceTotal);
}

function mapSwissFrancWasPrices(offer: ItemType): ItemType {
	const priceInfo = 'PackageOffer' in offer ? offer.PackageOffer?.PriceInformation : null;
	if (!priceInfo?.Converted?.ExchangeRate || determineClient() !== 'ch') {
		return offer;
	}

	const exchangeRate = priceInfo.Converted.ExchangeRate;
	const result = { ...offer };

	if (result.WasPrice) {
		const currentTotalPrice = Number(result.Pricing.Total.Converted || result.Pricing.Total.Amount);
		const convertedReferencePriceTotal = convertReferencePriceTotal(result.WasPrice.referencePriceTotal, exchangeRate);

		result.WasPrice = {
			...result.WasPrice,
			referencePriceTotal: convertedReferencePriceTotal,
			relativeDiscountPercent: convertedReferencePriceTotal
				? calculateDiscountPercent(currentTotalPrice, convertedReferencePriceTotal)
				: null,
		};
	}

	return result;
}

export function getDiscountHotelPriceOfferMap(offersData: ItemType[]): Map<string, DiscountHotelPriceOffer> {
	if (!offersData) {
		return new Map();
	}
	const offersWithWasPrice = offersData.filter((offer) => offer.WasPrice);
	return new Map<string, DiscountHotelPriceOffer>(
		offersWithWasPrice.map((offer) => {
			const convertedOffer = mapSwissFrancWasPrices(offer);
			const { IffCode } = convertedOffer.Hotel;
			const offerId =
				'PackageOffer' in convertedOffer ? convertedOffer.PackageOffer?.OfferID : convertedOffer.AccommodationOffer?.OfferID;

			return [
				getUniqueKey({ iffCode: IffCode, offerId }),
				{
					currentPrice: Number(convertedOffer.Pricing.Single.Converted || convertedOffer.Pricing.Single.Amount),
					iffCode: Number(convertedOffer.Hotel.IffCode),
					offerId: offerId,
					responseCode: 100,
					referencePriceTotal: convertedOffer.WasPrice?.referencePriceTotal ?? null,
					relativeDiscountPercent: convertedOffer.WasPrice?.relativeDiscountPercent ?? null,
				},
			];
		})
	);
}
